import type { PublishingOptionType } from '@ContractBuilder/types';
import type { BlockError } from '@domain/schemas/block-error';
import { DbEndorsementStatusSchema, DbTemplateStatusSchema, ResourceState } from '@domain/schemas/contract.schema';
import { UserRole } from '@domain/schemas/user-role';
import type { EntityData } from '@domain/types/entity';
import { ContractError } from '@domain/types/errors';
import { DocumentMode } from '@helpers/document-mode';
import { getAllBlocks } from '@root/helpers/blocks';
import { getAllowedEndorsementTargetStatuses } from '@root/helpers/endorsements/state-transitions';
import { isNonNullish } from '@root/helpers/isNonNullish';
import { getAllowedTemplateTargetStatuses } from '@root/helpers/templates/state-transitions';
import { get } from 'lodash-es';

export const getBlockValidationErrors = (isSubmission: boolean, submission?: EntityData) => {
  if (isSubmission) {
    const allBlocks = getAllBlocks(submission);
    const blockErrors = allBlocks.flatMap((block) => block.validationErrors);

    return [...new Set(blockErrors)].filter(isNonNullish);
  }

  return [];
};

export const getContractValidationErrors = (isSubmission: boolean, isValid: boolean, submission?: EntityData) => {
  if (isSubmission) {
    const result: { type: ContractError; error: Record<string, string>[] }[] = [];
    if (!isValid) {
      result.push({
        type: ContractError.InvalidForm,
        error: [], // TODO: add errors
      });
    }

    const hasMissingDataPoints = !!submission?.contract_missing_data?.length;
    if (hasMissingDataPoints) {
      result.push({ type: ContractError.MissingDatapoints, error: submission?.contract_missing_data ?? [] });
    }

    if (submission?.hasMaterialChanges === false) {
      result.push({ type: ContractError.NoMaterialChanges, error: [] });
    }

    return result;
  }

  return [];
};

export const isDocumentValid = (blockErrors: BlockError[], contractErrors: { type: ContractError }[]): boolean =>
  blockErrors.length === 0 && !contractErrors.some(({ type }) => type === ContractError.InvalidForm);

export const isCurrentStatus = (currentStatus?: string, status?: string) => status && currentStatus === status;
export const isFirmOrderStatus = (id: string) => id === 'FIRM_ORDER';
export const isDraftStatus = (status?: string) => status === 'DRAFT';
export const isReviewStatus = (status?: string) => status === 'REVIEW';

type AllowedStatusesMap = Partial<Record<ResourceState, ResourceState[]>>;

const getSubmissionAllowedStatuses = (isFirmOrderEnabled?: boolean): AllowedStatusesMap => ({
  ARCHIVED: [ResourceState.ARCHIVED, ResourceState.DRAFT],
  DRAFT: [ResourceState.ARCHIVED, ResourceState.DRAFT, ResourceState.REVIEW, ResourceState.PUBLISHED],
  REVIEW: [
    ResourceState.ARCHIVED,
    ResourceState.DRAFT,
    ResourceState.REVIEW,
    isFirmOrderEnabled ? ResourceState.FIRM_ORDER : ResourceState.FINAL,
  ],
  FINAL: [ResourceState.DRAFT, ResourceState.REVIEW, ResourceState.FIRM_ORDER, ResourceState.FINAL],
  FIRM_ORDER: [
    ResourceState.DRAFT,
    ResourceState.REVIEW,
    ResourceState.ARCHIVED,
    ResourceState.FIRM_ORDER,
    ResourceState.FINAL,
  ],
});

const DEFAULT_ALLOWED_STATUSES: AllowedStatusesMap = {
  ARCHIVED: [ResourceState.ARCHIVED, ResourceState.DRAFT],
  DRAFT: [ResourceState.ARCHIVED, ResourceState.DRAFT, ResourceState.FINAL, ResourceState.PUBLISHED],
  REVIEW: [ResourceState.ARCHIVED, ResourceState.DRAFT, ResourceState.REVIEW, ResourceState.FINAL],
  FINAL: [ResourceState.DRAFT, ResourceState.REVIEW, ResourceState.FIRM_ORDER, ResourceState.FINAL],
  FIRM_ORDER: [
    ResourceState.DRAFT,
    ResourceState.REVIEW,
    ResourceState.ARCHIVED,
    ResourceState.FIRM_ORDER,
    ResourceState.FINAL,
  ],
};

const getAllowedStatusesMap = ({
  documentMode,
  isAdmin,
  isEndorsementReviewEnabled,
  isFirmOrderEnabled,
  isSuperadmin,
  isTemplateReviewWorkflowEnabled,
}: {
  documentMode?: DocumentMode;
  isAdmin?: boolean;
  isEndorsementReviewEnabled?: boolean;
  isFirmOrderEnabled?: boolean;
  isSuperadmin?: boolean;
  isTemplateReviewWorkflowEnabled?: boolean;
}): AllowedStatusesMap => {
  if (documentMode === DocumentMode.TEMPLATES) {
    return Object.fromEntries(
      DbTemplateStatusSchema.options.map((status) => {
        const transitions = getAllowedTemplateTargetStatuses({
          sourceStatus: status,
          isTemplateReviewWorkflowEnabled: !!isTemplateReviewWorkflowEnabled,
          userRole: isSuperadmin ? UserRole.Superadmin : undefined,
        });
        return [status, [status, ...transitions]];
      }),
    ) as AllowedStatusesMap;
  }

  if (documentMode === DocumentMode.ENDORSEMENTS) {
    return Object.fromEntries(
      DbEndorsementStatusSchema.options.map((status) => [
        status,
        [status, ...getAllowedEndorsementTargetStatuses({ sourceStatus: status, isAdmin, isEndorsementReviewEnabled })],
      ]),
    ) as AllowedStatusesMap;
  }

  if (documentMode === DocumentMode.SUBMISSIONS) {
    return getSubmissionAllowedStatuses(isFirmOrderEnabled);
  }

  return DEFAULT_ALLOWED_STATUSES;
};

const getAllowedStatusesAsAdmin = (
  publishingOptions: PublishingOptionType[],
  currentStatus?: string,
  allowedStatusesForStatus: ResourceState[] = [],
) => {
  if (currentStatus === 'FINAL') {
    return publishingOptions.filter(({ id }) => id !== 'ARCHIVED');
  }

  return publishingOptions.filter(({ id }) => allowedStatusesForStatus.includes(id));
};

export const filterPublishingOptions = ({
  documentMode,
  isAdmin,
  isEndorsementReviewEnabled,
  isFirmOrderEnabled,
  isSuperadmin,
  isTemplateReviewWorkflowEnabled,
  publishingOptions,
  status,
}: {
  publishingOptions: PublishingOptionType[];
  status?: string;
  isFirmOrderEnabled?: boolean;
  isAdmin?: boolean;
  documentMode?: DocumentMode;
  isEndorsementReviewEnabled?: boolean;
  isSuperadmin?: boolean;
  isTemplateReviewWorkflowEnabled?: boolean;
}) => {
  const allowedStatuses = getAllowedStatusesMap({
    documentMode,
    isAdmin,
    isEndorsementReviewEnabled,
    isFirmOrderEnabled,
    isSuperadmin,
    isTemplateReviewWorkflowEnabled,
  });
  const allowedStatusesForStatus = get(allowedStatuses, status ?? '', []) as ResourceState[];

  if (!allowedStatusesForStatus.length) {
    return publishingOptions;
  }

  if (isAdmin) {
    return getAllowedStatusesAsAdmin(publishingOptions, status, allowedStatusesForStatus);
  }

  return publishingOptions.filter(({ id }) => allowedStatusesForStatus.includes(id));
};
